import { useState } from "react";
import { homeService } from "@miplatform/api";
import {
  COST_CALCULATION_TYPE,
  HTTP_STATUSES,
  PRINT_ORIENTATION,
} from "@miplatform/constants";
import { useComponentState } from "@miplatform/hooks";
import {
  ICategoryPricingDetailsResponse,
  IMaterialsResponse,
  ISubscriptionResponse,
  IUpdateCategoryDrawRequest,
} from "@miplatform/types";
import { useCustomerMaterials, usePrint } from "@mibuildweb/hooks";
import { groupAndSortMaterialsByCategory } from "../../CustomerPricingDetails/useCustomerPricingDetailsViewModel";

export const useCustomerItemsPricingTabViewModel = ({
  tenantDetails,
}: {
  tenantDetails: ISubscriptionResponse | null;
}) => {
  const [categoryPricingDetails, setCategoryPricingDetails] = useState<
    ICategoryPricingDetailsResponse[]
  >([]);
  const [groupedCustomerMaterials, setGroupedCustomerMaterials] = useState<
    Record<string, IMaterialsResponse[]>
  >({});

  const stateManager = useComponentState();

  const handleGetCategoryPricing = async (accountId: string) => {
    return stateManager.run(async () => {
      const response = await homeService.accountPricing.getAll({
        accountId,
      });
      if (response.status === HTTP_STATUSES.OK) {
        const mappedCategories = response.data.map(
          (category: ICategoryPricingDetailsResponse) => ({
            ...category,
            accountId,
            ...(category.costCalculation === COST_CALCULATION_TYPE.LIST && {
              multiplier: category.multiplier ?? 1.0,
            }),
            ...(category.costCalculation === COST_CALCULATION_TYPE.NET_COST && {
              markup: category.markup ?? 0.0,
            }),
          })
        );
        setCategoryPricingDetails(mappedCategories);
      }
    });
  };

  const handleGetCustomerMaterials = async (accountId: string) => {
    const { status, data }: { status: number; data: IMaterialsResponse[] } =
      await getCustomerMaterials({ accountId });

    if (status === HTTP_STATUSES.OK) {
      setCustomerMaterials(data);
      setGroupedCustomerMaterials(groupAndSortMaterialsByCategory(data));
    }
  };

  const handleUpdateCategoryModifiers = async (
    accountId: string,
    data: IUpdateCategoryDrawRequest[]
  ) => {
    return stateManager.run(async () => {
      const response = await homeService.accountPricing.updateAll({
        accountId,
        data,
        toastMessageConfig: {
          failure: {
            text: "Failed to update Category price modifiers.",
          },
        },
      });
      return response;
    });
  };

  const handleUpdateCustomerMaterials = async (
    accountId: string,
    data: Record<string, IMaterialsResponse[]>
  ) => {
    const materials = Object.values(data)
      .flat()
      .map((m) => ({
        id: m.id ?? m.materialId,
        netSalesPrice: m.netSalesPrice ?? null,
      }));
    const response: { status: number; data: IMaterialsResponse[] } =
      await updateCustomerMaterials(accountId, materials, {
        success: {
          text: "Customer Items pricing updated successfully.",
        },
        failure: {
          text: "Failed to update Customer Items pricing.",
        },
      });
    return response;
  };

  const getCategoryPricingDetailsWithMaterials = (values: {
    categoryPricingDetails: ICategoryPricingDetailsResponse[];
    groupedCustomerMaterials: Record<string, IMaterialsResponse[]>;
  }) => {
    const {
      categoryPricingDetails: currentCategoryPricingDetails,
      groupedCustomerMaterials: currentGroupedCustomerMaterials,
    } = values;

    return currentCategoryPricingDetails
      .map((category) => {
        const materialsByCategoryKey =
          currentGroupedCustomerMaterials[category.categoryId] ??
          currentGroupedCustomerMaterials[
            (category as { id?: string }).id ?? ""
          ] ??
          [];

        const materialsByCategoryId = Object.values(
          currentGroupedCustomerMaterials
        )
          .flat()
          .filter(
            (material) => material.category.categoryId === category.categoryId
          );

        const materials = (
          materialsByCategoryKey.length > 0
            ? materialsByCategoryKey
            : materialsByCategoryId
        ).map((material) => ({
          ...material,
          netSalesPrice: material.netSalesPrice ?? null,
          marginDollar: material.marginDollar ?? null,
          marginPercent: material.marginPercent ?? null,
        }));

        return {
          ...category,
          materials,
        };
      })
      .filter((category) => category.materials.length > 0);
  };

  const getItemPricingTotals = (
    itemPricingByCategory: Record<string, IMaterialsResponse[]>
  ) => {
    const materials = Object.values(itemPricingByCategory ?? {}).flat();

    const totalCost = Number(
      materials
        .reduce((sum, material) => sum + (material.cost ?? 0), 0)
        .toFixed(2)
    );
    const totalSalesPrice = Number(
      materials
        .reduce((sum, material) => sum + (material.netSalesPrice ?? 0), 0)
        .toFixed(2)
    );
    const totalMargin = Number((totalSalesPrice - totalCost).toFixed(2));

    return {
      totalCost,
      totalSalesPrice,
      totalMargin,
    };
  };

  const handlePrintItems = (jsx: React.ReactNode) => {
    handlePrintExtendedTemplate(jsx, {
      headerFields: [],
      orientation: PRINT_ORIENTATION.PORTRAIT,
      showPrintedDate: true,
      hideWatermark: false,
    });
  };

  const {
    customerMaterials,
    getCustomerMaterials,
    setCustomerMaterials,
    updateCustomerMaterials,
    isLoading: isCustomerMaterialsLoading,
  } = useCustomerMaterials();

  const { handlePrintExtendedTemplate } = usePrint(tenantDetails);

  return {
    customerMaterials,
    groupedCustomerMaterials,
    categoryPricingDetails,
    handleGetCategoryPricing,
    handleGetCustomerMaterials,
    handleUpdateCategoryModifiers,
    handleUpdateCustomerMaterials,
    handlePrintItems,
    getCategoryPricingDetailsWithMaterials,
    getItemPricingTotals,
    isLoading: stateManager.isBusy() || isCustomerMaterialsLoading,
  };
};
